import { Formik, Form, Field, ErrorMessage, FormikProps } from "formik";
import { LabUserResponse, useUserService } from "../../services/users";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";

import { getIndustryRoles } from "../../services/generalInfo";
import { TGenericGUIDName, TProcessStates } from "../../lib/types/general";
import { DentalTechSignUpFormData } from "../../lib/types/dentalTechs";
import { PillInput, usePillInput } from "../common/pillInput";
import { useRouteNavigation } from "../../lib/common/routes";
import { PositionsCheckList } from "../common/positions-check-list";
import { getRegions } from "../../services/regions";
import { emailRegex, isNumberFromString, phoneRegex } from "../../lib/common/validation";
import { Spinner } from "../common/spinner";
import { MIN_PASSWORD_LENGTH } from "../../lib/common/constants";
import { RegionsCheckList } from "../common/regions-check-list";
import { TEmployeeRole } from "../../lib/types/employee_roles";
import { evaluators, useWindowSizeMatch } from "../../lib/window-size-matcher";
import { DEVICES_SIZES_IN_PX } from "../../lib/common/constants";
import { PasswordInput } from "../common/password-input";
import { MESSAGES_MAP } from "../../services/i18n";
import { usa_flag } from "../../images/flags";

type SignupTechValues = DentalTechSignUpFormData & { confirmEmail: string };

export const SignupTechForm = () => {
    const [generalError, setGeneralError] = useState<string | undefined>(undefined);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [userCreated, setUserCreated] = useState(false);
    const [regions, setRegions] = useState<TGenericGUIDName[]>([]);
    // const okNotificationRef = useRef(null);
    const [industryRoles, setIndustryRoles] = useState<TEmployeeRole[]>([]);
    const [workingState, setWorkingState] = useState<TProcessStates>("idle");
    const { getRoute, navigateToRoute } = useRouteNavigation();
    const signUpRef = useRef<HTMLDivElement>(null);

    const hobbies = usePillInput([]);
    //const interests = usePillInput([]);

    const [progress, setProgress] = useState(0);
    const [currentCard, setCurrentCard] = useState(1);
    const totalCards = 5;

    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
    });

    useEffect(() => {
        getRegions()
            .then((data) => {
                setRegions(data);
                return getIndustryRoles();
            })
            .then((data) => {
                setIndustryRoles(data);
            }); //TODO: get a better error management
    }, []);

    const { signUpUser, configureNewTech } = useUserService();

    const userValidationHandler = async (values: DentalTechSignUpFormData): Promise<LabUserResponse> => {
        const signUpResponse = await signUpUser(values.email, values.password);
        if (signUpResponse.error) {
            setWorkingState("error");
            window.scrollTo({ top: 0 });
            setGeneralError(signUpResponse.error);
        }

        return signUpResponse;
    };

    const submitHandler = ({ confirmEmail: _confirmEmail, ...values }: SignupTechValues, { setSubmitting }: { setSubmitting: (val: boolean) => void }) => {
        setGeneralError(undefined);

        setTimeout(async () => {
            setWorkingState("working");

            const signUpResponse = await userValidationHandler(values);
            if (signUpResponse.error) {
                setSubmitting(false);
                return;
            }
            await configureNewTech({
                ...values,
                hobbies: hobbies.values,
                /*interests: interests.values,*/
                guid: signUpResponse.guid,
                roles: values.roles.map(Number),
                password2nd: undefined,
                zip: values.zip,
                desiredRegion: values.desiredRegion,
            });
            setUserCreated(true);
            localStorage.setItem("firstLogin", "true");
            localStorage.setItem("techName", values.firstName);
            localStorage.setItem("techLastName", values.lastName);
            setWorkingState("done");

            setTimeout(() => {
                navigateToRoute("dentalTechAccount");
                // uncomment okNotificationRef when tech has to pay subscription
                // (okNotificationRef.current as any).scrollIntoView({ behavior: 'smooth', block: 'start' });
                // (okNotificationRef.current as any).style.opacity = "1";
            }, 100);

            setSubmitting(false);
        }, 400);
    };

    // uncomment okDisplay when tech has to pay subscription
    // const OkDisplay = () => {
    //     const { navigateToRoute, getRoute, navigateRouteClickHandler } = useRouteNavigation();

    //     setTimeout(() => {
    //         navigateToRoute('subscriptionSelection');
    //     }, 10000);

    //     return (

    //         <div ref={okNotificationRef} className="text-center" style={{ "transition": "all 250ms linear 250ms", "opacity": "0" }}>
    //             <i className="fa-regular fa-circle-check text-success my-5" style={{ 'fontSize': '7rem' }}></i>
    //             <h3>🎉 Registration Successful!</h3>
    //             <h3>Let's choose your <a href={getRoute('subscriptionSelection')}
    //                 onClick={(e) => navigateRouteClickHandler('subscriptionSelection', e)}>subscription plan.</a></h3>
    //         </div>
    //     )
    // }

    const validationSchemas = [
        // Slide 1: Name and TC
        Yup.object().shape({
            firstName: Yup.string().required("Required"),
            lastName: Yup.string().required("Required"),
            phone: Yup.string()
                .matches(phoneRegex, "Enter a valid 10-digit US phone number")
                .required("Mobile phone number is required for SMS verification and account notifications."),
            zip: Yup.string()
                .test("is-number-from-string", "ZIP must be a number", (value) => (value ? isNumberFromString(value) : true))
                .required("Required"),
            accept: Yup.boolean().oneOf([true], "Please accept the terms and conditions"),
            smsConsent: Yup.boolean().oneOf([true], "You must agree to receive SMS notifications to continue."),
        }),
        // Slide 2: Job Roles
        Yup.object().shape({
            yearsOfExperience: Yup.number()
                .nullable()
                .typeError("Must be a number from 0-100")
                .integer("Must be a whole number")
                .min(0, "Must be a number from 0-100")
                .max(100, "Must be a number from 0-100"),
            roles: Yup.array().of(Yup.number()).min(1, "At least one role is required").required("At least one role is required"),
            desiredSalaryPerYear: Yup.object().shape({
                amount: Yup.string()
                    .nullable()
                    .test("is-number-from-string", "Desired Pay must be a number", (value) => (!value ? true : isNumberFromString(value))),
                frequency: Yup.string().when("amount", {
                    is: (val: null | undefined) => val !== null && val !== undefined,
                    then: (schema) => schema.required("You should select a time frame").oneOf(["yearly", "hourly"], "You should select a valid time frame"),
                    otherwise: (schema) => schema.notRequired().nullable(),
                }),
            }),
        }),
        // Slide 3: Desired Region
        Yup.object().shape({
            desiredRegion: Yup.array().of(Yup.string()).min(1, "At least one region is required").required("At least one region is required"),
        }),
        // Slide 4: Interests & Hobbies
        Yup.object().shape({}),
        // Slide 5: Account Information
        Yup.object().shape({
            email: Yup.string().required("Required").matches(emailRegex, "Invalid email format"),
            confirmEmail: Yup.string().required("Required").oneOf([Yup.ref("email")], "Email addresses must match"),
            password: Yup.string().required("Required").min(MIN_PASSWORD_LENGTH, MESSAGES_MAP["auth/weak-password"]),
            password2nd: Yup.string()
                .oneOf([Yup.ref("password"), undefined], "Passwords must match")
                .required("Required"),
        }),
    ];

    const progressAction = async (direction: "next" | "prev", validateForm?: any) => {
        const errors = await validateForm();
        if (direction === "next" && currentCard < totalCards) {
            if (Object.keys(errors).length === 0) {
                const newCard = currentCard + 1;
                setCurrentCard(newCard);
                setProgress((newCard / totalCards) * 100);
            }
        } else if (direction === "prev" && currentCard > 1) {
            const newCard = currentCard - 1;
            setCurrentCard(newCard);
            setProgress((newCard / totalCards) * 100);
        }

        signUpRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <div ref={signUpRef}>
            {/* uncomment when tech has to pay subscription
            {(userCreated) ? (<OkDisplay />) : ( */}
            <Formik
                initialValues={{
                    guid: "",
                    email: "",
                    confirmEmail: "",
                    password: "",
                    password2nd: "",
                    lastName: "",
                    firstName: "",
                    yearsOfExperience: null,
                    desiredSalaryPerYear: { amount: null, frequency: "yearly" },
                    desiredRegion: [],
                    position_0: false,
                    roles: [],
                    accept: false,
                    zip: "",
                    phone: "",
                    smsConsent: false,
                }}
                validationSchema={validationSchemas[currentCard - 1]}
                onSubmit={submitHandler}
            >
                {({ isSubmitting, values, handleChange, validateForm, errors }) => (
                    <Form>
                        <div className="progress my-3 mx-auto signup-width px-0">
                            <div
                                className="progress-bar rounded"
                                style={{ width: `${progress}%` }}
                                role="progressbar"
                                aria-label="Tech Sign Up Progress"
                                aria-valuenow={progress}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            >
                                {progress}%
                            </div>
                        </div>
                        <div className="row justify-content-center">
                            <div id="techSignUpCarousel" className="carousel carousel-dark slide signup-width" data-touch="false" data-interval="false">
                                <div className="carousel-inner">
                                    {validationSchemas.map((validationSchema, index) => (
                                        <div key={index} className={`carousel-item ${currentCard === index + 1 ? "active" : ""}`}>
                                            <div className="card py-3">
                                                {index === 0 && (
                                                    <>
                                                        <h5 className="text-center p-3">👋 Welcome to WinWinGo!</h5>
                                                        <p className="text-center px-3">Let's get started, we want to get to know you better.</p>
                                                        <div className="card-body">
                                                            <div className="row mb-3">
                                                                <div className="col-6 col-md-3">
                                                                    <h6>
                                                                        <label className="form-label" htmlFor="First Name">
                                                                            First Name *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-6 d-block d-md-none">
                                                                    {errors.firstName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.firstName}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field
                                                                        id="First Name"
                                                                        name="firstName"
                                                                        type="text"
                                                                        className="form-control mb-2"
                                                                        maxLength={25}
                                                                    />
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.firstName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.firstName}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-6 col-md-3">
                                                                    <h6>
                                                                        <label htmlFor="Last Name" className="form-label">
                                                                            Last Name *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-6 d-block d-md-none">
                                                                    {errors.lastName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.lastName}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field
                                                                        id="Last Name"
                                                                        name="lastName"
                                                                        type="text"
                                                                        className="form-control mb-2"
                                                                        maxLength={25}
                                                                    />
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.lastName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.lastName}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-12 col-md-3">
                                                                    <h6 className="m-0 mb-2">
                                                                        <label htmlFor="phone" className="form-label mb-0">
                                                                            Mobile Phone Number *
                                                                        </label>{" "}
                                                                        {""}
                                                                        <span className="form-text fw-light">Example: 5551234567</span>
                                                                    </h6>
                                                                </div>
                                                                <div className="col d-block d-md-none">
                                                                    {errors.phone ? (
                                                                        <div
                                                                            className="alert alert-warning py-1 px-2 mt-1 rounded-3 fw-bold"
                                                                            style={{ fontSize: "x-small" }}
                                                                        >
                                                                            <i className="fa-solid fa-circle-exclamation me-1"></i>
                                                                            {errors.phone}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <div className="input-group">
                                                                        <div className="input-group-text bg-light">
                                                                            <img src={usa_flag} alt="usa_flag" className="me-2 me-lg-0" width={20} />
                                                                            <span className="text-muted ms-md-2">+1</span>
                                                                        </div>
                                                                        <Field name="phone">
                                                                            {({ form }: { form: FormikProps<DentalTechSignUpFormData> }) => (
                                                                                <input
                                                                                    id="phone"
                                                                                    name="phone"
                                                                                    type="tel"
                                                                                    className="form-control"
                                                                                    maxLength={10}
                                                                                    autoComplete="tel-national"
                                                                                    inputMode="numeric"
                                                                                    value={form.values.phone || ""}
                                                                                    onChange={(e) => {
                                                                                        form.setFieldValue(
                                                                                            "phone",
                                                                                            e.target.value.replace(/\D/g, "").slice(0, 10),
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.phone ? (
                                                                        <div
                                                                            className="alert alert-warning py-1 px-2 rounded-3 fw-bold"
                                                                            style={{ fontSize: "x-small" }}
                                                                        >
                                                                            <i className="fa-solid fa-circle-exclamation me-1"></i>
                                                                            {errors.phone}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-6 col-md-3">
                                                                    <h6 className="mb-0">
                                                                        <label className="form-label" htmlFor="Zip">
                                                                            ZIP Code *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-6 d-block d-md-none mb-2 mb-md-0">
                                                                    {errors.zip ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">{errors.zip}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field id="Zip" name="zip" type="text" className="form-control" maxLength={10} />
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.zip ? <div className="badge rounded-pill text-bg-warning">{errors.zip}</div> : null}
                                                                </div>
                                                            </div>
                                                            <hr />
                                                            <div className="row">
                                                                <div className="col-md-10">
                                                                    <div className="form-check">
                                                                        <input
                                                                            name="smsConsent"
                                                                            id="smsConsent"
                                                                            type="checkbox"
                                                                            className="form-check-input mt-1"
                                                                            checked={values.smsConsent}
                                                                            onChange={handleChange}
                                                                        />
                                                                        <label
                                                                            className="form-label lh-md"
                                                                            style={{ fontSize: "0.75rem" }}
                                                                            htmlFor="smsConsent"
                                                                        >
                                                                            By providing your phone number, you agree to receive SMS notifications from WinWinGo
                                                                            related to account activity, job connections, and verification codes. Message and
                                                                            data rates may apply depending on your carrier.
                                                                            <br />
                                                                            Reply STOP to unsubscribe.
                                                                            <br />
                                                                            See our{" "}
                                                                            <a
                                                                                href={getRoute("privacyPolicy")}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="text-primary"
                                                                            >
                                                                                Privacy Policy
                                                                            </a>{" "}
                                                                            and{" "}
                                                                            <a
                                                                                href={getRoute("termsOfUse")}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="text-primary"
                                                                            >
                                                                                Terms of Use
                                                                            </a>
                                                                            .
                                                                        </label>
                                                                    </div>
                                                                    {errors.smsConsent ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">{errors.smsConsent}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-12 col-md-9 pt-4">
                                                                    <div className="form-check">
                                                                        <input
                                                                            name="accept"
                                                                            id="accept_0"
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            checked={values.accept}
                                                                            onChange={handleChange}
                                                                            aria-describedby="acceptHelpBlock"
                                                                        />
                                                                        <label className="h6 form-label">
                                                                            I accept the
                                                                            <a
                                                                                className="text-primary"
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                href={getRoute("privacyPolicy")}
                                                                            >
                                                                                <strong> Privacy Policy </strong>
                                                                            </a>
                                                                            and
                                                                            <a
                                                                                className="text-primary"
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                href={getRoute("termsOfUse")}
                                                                            >
                                                                                <strong> Terms of Use</strong>
                                                                            </a>
                                                                            . *
                                                                        </label>
                                                                    </div>
                                                                    <span id="acceptHelpBlock" className="form-text text-muted"></span>
                                                                </div>
                                                                <div className="col mt-2">
                                                                    {errors.accept ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.accept}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {index === 1 && (
                                                    <>
                                                        <h5 className="text-center py-3">💼 Your Work Background</h5>
                                                        <p className="text-center px-3">Share your experience and what job you're aiming for.</p>
                                                        <div className="card-body">
                                                            <div className="row mb-3">
                                                                <div className="col-md-4">
                                                                    <h6 className="pb-2">
                                                                        <label htmlFor="Years of Experience" className="form-label">
                                                                            Years of Experience
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-md-3 mb-2 mb-md-0">
                                                                    <Field
                                                                        id="Years of Experience"
                                                                        name="yearsOfExperience"
                                                                        type="text"
                                                                        className="form-control"
                                                                        maxLength={3}
                                                                    />
                                                                </div>
                                                                <div className="col-md-5 mb-3">
                                                                    {errors.yearsOfExperience ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                            {errors.yearsOfExperience}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            <div className="row mb-5">
                                                                <div className="col-12 ">
                                                                    <label className="h6 form-label my-2">Roles Applying For *</label>
                                                                </div>
                                                                <div className="mb-3">
                                                                    {errors.roles ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.roles}</div>
                                                                    ) : null}
                                                                </div>
                                                                <PositionsCheckList fieldName="roles" roles={industryRoles} />
                                                            </div>

                                                            <div className="row mb-3">
                                                                <div className="col-md-4">
                                                                    <h6 className="py-2">
                                                                        <label htmlFor="desiredSalaryPerYear.amount" className="form-label">
                                                                            Desired Pay
                                                                        </label>
                                                                    </h6>
                                                                </div>

                                                                <div className="col-md-6 d-flex align-items-start gap-2">
                                                                    <span className="fw-bold pt-2">$</span>

                                                                    <div>
                                                                        <Field
                                                                            id="desiredSalaryPerYear.amount"
                                                                            name="desiredSalaryPerYear.amount"
                                                                            type="text"
                                                                            className="form-control"
                                                                            maxLength={25}
                                                                            placeholder="Enter amount"
                                                                        />
                                                                        <ErrorMessage
                                                                            name="desiredSalaryPerYear.amount"
                                                                            component="div"
                                                                            className="badge rounded-pill text-bg-warning text-wrap mt-2"
                                                                        />
                                                                    </div>

                                                                    <div className="flex-shrink-1">
                                                                        <Field
                                                                            as="select"
                                                                            id="desiredSalaryPerYear.frequency"
                                                                            name="desiredSalaryPerYear.frequency"
                                                                            className="form-select w-auto"
                                                                        >
                                                                            <option value="">Select</option>
                                                                            <option value="yearly">Yearly</option>
                                                                            <option value="hourly">Hourly</option>
                                                                        </Field>
                                                                        <ErrorMessage
                                                                            name="desiredSalaryPerYear.frequency"
                                                                            component="div"
                                                                            className="badge rounded-pill text-bg-warning text-wrap mt-2"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {index === 2 && (
                                                    <>
                                                        <h5 className="text-center p-3">🌎 Where Do You Want to Work?</h5>
                                                        <p className="text-center px-3">Pick the region where you'd like to work.</p>
                                                        <div className="card-body">
                                                            <div className="row mb-5">
                                                                <div className="col-12 mb-3">
                                                                    {errors.desiredRegion ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                            {errors.desiredRegion}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                                <RegionsCheckList regions={regions} masterKey="techSignupFormik" />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {index === 3 && (
                                                    <>
                                                        <h5 className="text-center p-3">
                                                            <i className="fa-solid fa-wand-magic-sparkles me-3 text-primary"></i> Add Your Hobbies & Interests
                                                        </h5>
                                                        <p className="text-center px-3">
                                                            Add a few hobbies and interests so our AI can suggest dental employers that are a better fit for
                                                            you. <br /> You can choose up to 10. If you do not see yours, you can also add your own tags.
                                                        </p>
                                                        <div className="card-body">
                                                            <div className="rounded-4 bg-light p-2 p-md-4">
                                                                <PillInput hook={hobbies} />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {index === 4 && (
                                                    <>
                                                        <h5 className="text-center p-3">Almost Done! 🎉</h5>
                                                        <p className="text-center px-3">Let's choose your email and a secure password.</p>
                                                        <div className="card-body">
                                                            <div className="row mb-3">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <label className="form-label small fw-semibold text-primary ms-2" htmlFor="Email">
                                                                        Email *
                                                                    </label>
                                                                    <Field id="Email" name="email" type="email" className="form-control mb-2" maxLength={254} />
                                                                    {errors.email && (
                                                                        <small className="badge rounded-pill text-bg-warning text-wrap">{errors.email}</small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <label className="form-label small fw-semibold text-primary ms-2" htmlFor="ConfirmEmail">
                                                                        Confirm Email *
                                                                    </label>
                                                                    <Field
                                                                        id="ConfirmEmail"
                                                                        name="confirmEmail"
                                                                        type="text"
                                                                        inputMode="email"
                                                                        className="form-control mb-2"
                                                                        maxLength={254}
                                                                        autoComplete="new-password"
                                                                        autoCapitalize="none"
                                                                        spellCheck={false}
                                                                        onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => event.preventDefault()}
                                                                        onDrop={(event: React.DragEvent<HTMLInputElement>) => event.preventDefault()}
                                                                    />
                                                                    {errors.confirmEmail && (
                                                                        <small className="badge rounded-pill text-bg-warning text-wrap">{errors.confirmEmail}</small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <PasswordInput
                                                                        label="Password *"
                                                                        value={values.password}
                                                                        onChange={(val) => handleChange("password")(val)}
                                                                        helperText={errors.password}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <PasswordInput
                                                                        label="Confirm Password *"
                                                                        value={values.password2nd}
                                                                        onChange={(val) => handleChange("password2nd")(val)}
                                                                        helperText={errors.password2nd}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="row mx-1">
                                                                {generalError ? (
                                                                    <div className="badge rounded-pill text-bg-danger text-wrap">{generalError}</div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {!isSmallDevice.fulfilled && (
                            <div className="row gap-4 justify-content-center mt-3">
                                {/* {currentCard === 1 && (
                                <button className="btn btn-primary col-lg-5"
                                    type="button"
                                    onClick={() => userValidationHandler(values, validateForm)}>
                                    <span className="" aria-hidden="true">Sign Up</span>
                                </button>
                            )} */}
                                {currentCard > 1 && (
                                    <button
                                        className="btn btn-outline-dark col-md-4 col-lg-3"
                                        type="button"
                                        onClick={() => progressAction("prev", validateForm)}
                                    >
                                        <span className="" aria-hidden="true">
                                            Back
                                        </span>
                                        <span className="visually-hidden">Previous</span>
                                    </button>
                                )}
                                {currentCard < totalCards && (
                                    <button className="btn btn-primary col-md-7 col-lg-5" type="button" onClick={() => progressAction("next", validateForm)}>
                                        <span className="" aria-hidden="true">
                                            Next
                                        </span>
                                        <span className="visually-hidden">Next</span>
                                    </button>
                                )}
                                {currentCard === totalCards && (
                                    <button disabled={isSubmitting} name="submit" type="submit" className="btn btn-primary col-md-7 col-lg-5">
                                        {workingState === "working" ? <Spinner /> : "Get Started"}
                                    </button>
                                )}
                            </div>
                        )}
                        {isSmallDevice.fulfilled && (
                            <div className="row gap-4 justify-content-center mt-3 mx-0">
                                {currentCard < totalCards && (
                                    <button
                                        className="btn btn-primary btn-lg rounded-2 shadow position-relative px-5"
                                        type="button"
                                        onClick={() => progressAction("next", validateForm)}
                                    >
                                        <span className=" position-relative" aria-hidden="true">
                                            Next
                                        </span>
                                        <i className="fa-solid fa-circle-right fa-lg position-absolute end-0 top-50 translate-middle-y me-3"></i>
                                        <span className="visually-hidden">Next</span>
                                    </button>
                                )}
                                {currentCard === totalCards && (
                                    <button disabled={isSubmitting} name="submit" type="submit" className="btn btn-primary btn-lg rounded-2 shadow">
                                        {workingState === "working" ? <Spinner /> : "Get Started"}
                                    </button>
                                )}
                                {currentCard > 1 && (
                                    <button
                                        className="btn btn-light shadow position-relative px-5"
                                        type="button"
                                        onClick={() => progressAction("prev", validateForm)}
                                    >
                                        <i className="fa-solid fa-circle-left fa-lg position-absolute start-0 top-50 translate-middle-y ms-3"></i>
                                        <span className="" aria-hidden="true">
                                            Back
                                        </span>
                                        <span className="visually-hidden">Previous</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
            {/* )
            } */}
        </div>
    );
};
